import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { Accordion } from 'components/molecules/Accordion';
import { useForm } from 'react-hook-form';
import {
  EgoodReasonValues,
  EsafeStorageValues,
  ILicenceTemplateApplicationProcessResponse,
  ILicenceTemplateResponse,
  IOwnerIndividual,
  IOwnerOrganisation,
  IPrintoutSettingsGeneral,
  IPrintoutSettingsConditions,
  IPrintoutSettingsOwner,
  IPrintoutSettingsWeaponData,
} from 'models/licenceConfigurator';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { CardList, emptyFieldHolder } from 'components/molecules/CardList';
import { getHtmlText } from 'components/organisms/HtmlSlate';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { EButtonVariants } from 'constants/Buttons';
import { useNavigate, useParams } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { EIconTypes } from 'constants/Icons';
import Button from 'components/atoms/Button';
import { useLang } from 'models/langContext';
import { Table } from 'components/molecules/Table';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Loader } from 'components/atoms/Loader';
import { useGlobalProperty } from 'models/globalPropertyContext';
import FormLabel from 'components/atoms/FormLabel';
import { Alert } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { clearAlertTimeout, EAlertTime } from 'utils/date';
import { LETypes } from 'constants/LegalEntityTypes';
import { ActionTypes } from 'state/actions/alert';
import {
  ELicenceCardInformationFormLabels,
  ELicenceFormLabels,
  getStatusVariant,
  LicenceApplicationProcessTableColumns,
  mapLicenceApplicationProcessToDataSource,
  ownerLegalEntityIndividualDataFields,
  ownerLegalEntityOrganisationDataFields,
  printoutSettingsConditionsFields,
  printoutSettingsGeneralFields,
  printoutSettingsOwnerFields,
  printoutSettingsWeaponDataFields,
} from '../helper';
import { StyledLicenceWrapper } from './styles';
import { useConfigurationLicence } from '../hooks';
import { Chips } from 'components/atoms/Chips';
import { LicenceApplicationProcessTableCard } from './LicenceApplicationProcessTableCard';
import { findInDictionary } from 'helpers/dictionary';
import { FormModal } from 'components/molecules/Modal';
import { EPermission } from 'models/permissions';

interface IDetailLicenceTemplate {
  licence: ILicenceTemplateResponse;
  licencePrices: ILicenceTemplateApplicationProcessResponse[];
  isProcessingPricingLoading: boolean;
  weaponLicenceTemplateStatus: IDictionary[] | null;
}

const DetailLicenceTemplatePage = ({
  licence,
  licencePrices,
  isProcessingPricingLoading,
  weaponLicenceTemplateStatus,
}: IDetailLicenceTemplate) => {
  const { t } = useTranslations();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { applicationProcessAlert, clearAlert } = useAlert();
  const { selectedLanguage } = useLang();
  const { getProperties, currency } = useGlobalProperty();
  const [processIdDelete, setProcessIdDelete] = useState<string | null>(null);
  const {
    weaponLicenceGoodReason,
    applicationProcessTypes,
    applicationProcessStatus,
    loadDictionaries,
    handleDeleteLicencePrice,
    preparePrintoutSettingsFields,
    generalFields,
    conditionsFields,
    ownerFields,
    weaponDataFields,
  } = useConfigurationLicence();
  const { isDesktop } = useThemeBreakpoint();

  const handleDeleteApplicationProcess = async () => {
    if (processIdDelete) {
      await handleDeleteLicencePrice(processIdDelete);
      setProcessIdDelete(null);
    }
  };

  useEffect(
    () => {
      clearAlertTimeout(
        clearAlert,
        ActionTypes.CLEAR_APPLICATION_PROCESS_ALERT,
        EAlertTime.SHORT,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (applicationProcessAlert) {
      window.scrollTo({
        top: window.scrollY,
        behavior: 'auto',
      });
    }
  }, [applicationProcessAlert]);

  useEffect(() => {
    if (id) {
      preparePrintoutSettingsFields(id);
    }
  }, [id]);

  // const filteredWeaponDataFields = weaponDataFields.filter(
  //   (field: string) => licence.documentInformation[field as keyof IDocumentInformation],
  // );

  // const filteredLicenceDataFields = licenceDataFields.filter(
  //   (field: string) => licence.documentInformation[field as keyof IDocumentInformation],
  // );

  // const filteredLegalEntityDataFields = legalEntityDataFields.filter(
  //   (field: string) => licence.documentInformation[field as keyof IDocumentInformation],
  // );

  const filteredGeneralDataFields = printoutSettingsGeneralFields.filter(
    (field: string) =>
      licence?.printoutSettings?.general[field as keyof IPrintoutSettingsGeneral],
  );

  const filteredConditionsDataFields = printoutSettingsConditionsFields.filter(
    (field: string) =>
      licence.printoutSettings?.conditions[field as keyof IPrintoutSettingsConditions],
  );

  const filteredOwnerDataFields = printoutSettingsOwnerFields.filter(
    (field: string) =>
      licence.printoutSettings?.licenceOwner[field as keyof IPrintoutSettingsOwner],
  );
  const filteredWeaponDataFields = printoutSettingsWeaponDataFields.filter(
    (field: string) =>
      licence.printoutSettings?.weaponData[field as keyof IPrintoutSettingsWeaponData],
  );

  const filteredOwnerIndividualDataFields = ownerLegalEntityIndividualDataFields.filter(
    (field: string) => licence.owner.individual[field as keyof IOwnerIndividual],
  );

  const filteredOwnerOrganisationDataFields = ownerLegalEntityOrganisationDataFields.filter(
    (field: string) => licence.owner.organisation[field as keyof IOwnerOrganisation],
  );

  useEffect(() => {
    (async function init() {
      await loadDictionaries([
        EDictionaryTypes.weaponLicenceGoodReason,
        EDictionaryTypes.applicationProcessTypes,
        EDictionaryTypes.applicationProcessStatus,
      ]);
      getProperties();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const {
    control,
    setValue,
    formState: { errors },
  } = useForm<ILicenceTemplateResponse>({
    defaultValues: licence,
  });

  useEffect(() => {
    if (licence.conditions?.goodReason === null) {
      setValue('conditions.goodReason', 'false');
    }
    if (licence.conditions?.safeStorage === null) {
      setValue('conditions.safeStorage', 'false');
    }
  }, [licence, setValue]);

  const noOptionsSelectedText = (
    <Box sx={{ width: '100%' }}>
      <Typography sx={{ fontStyle: 'italic' }}>
        {t('licenceDetails.noOptionsSelected.text')}
      </Typography>
    </Box>
  );

  return (
    <>
      <Accordion
        title={t('licence.generalSettings.label')}
        name="generalSettings"
        key="generalSettings"
        alwaysExpanded
        hideExpandIcon
      >
        <StyledLicenceWrapper container size={{ xs: 12 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t(ELicenceFormLabels.codeAndName)}
              </Typography>
              <Typography variant="body2">{`${licence?.general.code} ${licence?.general.name}`}</Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Stack>
              {licence?.general.licenceDurationUnlimited ? (
                <Grid size={{ xs: 12, md: 4 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(ELicenceFormLabels.licenceDuration)}
                    </Typography>
                    <Typography variant="body2">
                      {t(ELicenceFormLabels.licenceDurationUnlimited)}
                    </Typography>
                  </Stack>
                </Grid>
              ) : (
                <Grid size={{ xs: 12, md: 4 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(ELicenceFormLabels.licenceDuration)}
                    </Typography>
                    <Typography variant="body2">
                      {licence?.general.licenceDurationYears
                        ? `${licence?.general.licenceDurationYears} ${t(
                            'licenceDetails.years.label',
                          )}`
                        : ''}
                      {licence?.general.licenceDurationMonths
                        ? ` ${licence?.general.licenceDurationMonths} ${t(
                            'licenceDetails.month.label',
                          )}`
                        : ''}
                      {licence?.general.licenceDurationDays
                        ? ` ${licence?.general.licenceDurationDays} ${t(
                            'licenceDetails.day.label',
                          )}`
                        : ''}
                    </Typography>
                  </Stack>
                </Grid>
              )}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t(ELicenceFormLabels.definitionStatus)}
              </Typography>
              <Typography variant="body2">
                <Chips
                  text={findInDictionary(licence.statusUniqueId, weaponLicenceTemplateStatus)}
                  variant={getStatusVariant(licence.statusUniqueId)}
                />
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t(ELicenceFormLabels.description)}
              </Typography>
              {getHtmlText(licence?.general.description)}
            </Stack>
          </Grid>
        </StyledLicenceWrapper>
      </Accordion>
      <Accordion
        title={t('licence.licenceOwner.label')}
        name="licenceParameters"
        key="licenceParameters"
        alwaysExpanded
        hideExpandIcon
      >
        {licence?.owner.legalTypes.includes(LETypes.individual) && (
          <StyledLicenceWrapper container size={{ xs: 12 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(ELicenceFormLabels.relatedLegalEntityType)}
                </Typography>
                <Typography variant="body2">
                  {t('licence.userType.individual.label')}
                </Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <FormLabel
                name="ownerLegalEntityIndividualData"
                label={t('licence.legalEntityIndividualData.label')}
              />
              {filteredOwnerIndividualDataFields.length > 0
                ? filteredOwnerIndividualDataFields.map((item: string) => (
                    <FormCheckbox
                      name={`owner.individual.${item}`}
                      label={t(
                        ELicenceCardInformationFormLabels?.[
                          item as keyof typeof ELicenceCardInformationFormLabels
                        ],
                      )}
                      control={control}
                      errors={errors}
                      withValidation={false}
                      disabled
                      labelEnable
                    />
                  ))
                : noOptionsSelectedText}
            </Grid>
            {licence?.owner.individual.ageLimit ? (
              <Grid size={{ xs: 12, md: 4 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(ELicenceFormLabels.ageLimit)}
                  </Typography>
                  <Typography variant="body2">
                    {`${emptyFieldHolder(licence?.owner.individual.ageLimit)} ${
                      licence?.owner.individual.ageLimit ? t('general.yearYears.label') : ''
                    }`}
                  </Typography>
                </Stack>
              </Grid>
            ) : (
              ''
            )}
          </StyledLicenceWrapper>
        )}
        {licence?.owner.legalTypes.includes(LETypes.organization) && (
          <StyledLicenceWrapper container size={{ xs: 12 }}>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(ELicenceFormLabels.relatedLegalEntityType)}
                </Typography>
                <Typography variant="body2">
                  {t('licence.userType.organization.label')}
                </Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <FormLabel
                name="ownerLegalEntityOrganisationData"
                label={t('licence.legalEntityOrganisationData.label')}
              />
              {filteredOwnerOrganisationDataFields.length > 0
                ? filteredOwnerOrganisationDataFields.map((item: string) => (
                    <FormCheckbox
                      name={`owner.organisation.${item}`}
                      label={t(
                        ELicenceCardInformationFormLabels?.[
                          item as keyof typeof ELicenceCardInformationFormLabels
                        ],
                      )}
                      control={control}
                      errors={errors}
                      withValidation={false}
                      disabled
                      labelEnable
                    />
                  ))
                : noOptionsSelectedText}
            </Grid>
          </StyledLicenceWrapper>
        )}
      </Accordion>
      <Accordion
        title={t('licence.licenceConditions.label')}
        name="licenceConditions"
        key="licenceConditions"
        alwaysExpanded
        hideExpandIcon
      >
        <StyledLicenceWrapper container size={{ xs: 12 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormRadioGroup
              name="conditions.specificCountryArea"
              label={t(ELicenceFormLabels.specificCountryArea)}
              options={[
                { label: t('licence.areaRestriction.national'), value: 'true' },
                { label: t('licence.areaRestriction.specificArea'), value: 'false' },
              ]}
              control={control}
              errors={errors}
              disabled
              booleanType
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormRadioGroup
              name="conditions.competency"
              label={t(ELicenceFormLabels.competency)}
              options={[
                { label: t('general.yes.label'), value: 'true' },
                { label: t('general.no.label'), value: 'false' },
              ]}
              control={control}
              errors={errors}
              disabled
              booleanType
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormRadioGroup
              name="conditions.safeStorage"
              label={t(ELicenceFormLabels.safeStorage)}
              options={[
                { label: t('general.no.label'), value: 'false' },
                {
                  label: t(ELicenceFormLabels.safeStorageText),
                  value: EsafeStorageValues.Text,
                },
                {
                  label: t(ELicenceFormLabels.safeStorageObject),
                  value: EsafeStorageValues.Object,
                },
              ]}
              control={control}
              errors={errors}
              disabled
              booleanType
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormRadioGroup
              name="conditions.weaponCategory"
              label={t(ELicenceFormLabels.weaponCategory)}
              options={[
                { label: t('general.yes.label'), value: 'true' },
                { label: t('general.no.label'), value: 'false' },
              ]}
              control={control}
              errors={errors}
              disabled
              booleanType
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormRadioGroup
              name="conditions.weaponNumberLimit"
              label={t(ELicenceFormLabels.weaponNumberLimit)}
              options={[
                { label: t('general.yes.label'), value: 'true' },
                { label: t('general.no.label'), value: 'false' },
              ]}
              control={control}
              errors={errors}
              disabled
              booleanType
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormRadioGroup
              name="conditions.carryConditions"
              label={t(ELicenceFormLabels.carryConditions)}
              options={[
                { label: t('general.yes.label'), value: 'true' },
                { label: t('general.no.label'), value: 'false' },
              ]}
              control={control}
              errors={errors}
              disabled
              booleanType
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormRadioGroup
              name="conditions.additionalConditions"
              label={t(ELicenceFormLabels.additionalConditions)}
              options={[
                { label: t('general.yes.label'), value: 'true' },
                { label: t('general.no.label'), value: 'false' },
              ]}
              control={control}
              errors={errors}
              disabled
              booleanType
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormRadioGroup
              name="conditions.goodReason"
              label={t(ELicenceFormLabels.goodReason)}
              disabled
              options={[
                { label: t('general.no.label'), value: 'false' },
                { label: t(ELicenceFormLabels.goodReasonText), value: EgoodReasonValues.Text },
                {
                  label: t(ELicenceFormLabels.goodReasonDictionary),
                  value: EgoodReasonValues.Dictionary,
                },
              ]}
              control={control}
              errors={errors}
            />
          </Grid>
          {licence.conditions.goodReason === EgoodReasonValues.Dictionary && (
            <Grid size={{ xs: 12 }}>
              <Grid sx={{ gap: 1, alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(ELicenceFormLabels.goodReasonDictionaryIds)}
                </Typography>
              </Grid>
              <Grid container size={{ xs: 12 }}>
                {weaponLicenceGoodReason &&
                  weaponLicenceGoodReason
                    .filter((dictionary) =>
                      licence.conditions.goodReasonDictionaryIds.includes(
                        dictionary.uniqueId as string,
                      ),
                    )
                    .map((item: IDictionary) => (
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <FormCheckbox
                          name={`conditions.goodReasonDictionaryIds.${item.uniqueId}`}
                          label={item.name}
                          control={control}
                          errors={errors}
                          withValidation={false}
                          booleanType
                          disabled
                          checked={licence.conditions.goodReasonDictionaryIds.includes(
                            item.uniqueId as string,
                          )}
                        />
                      </Grid>
                    ))}
              </Grid>
            </Grid>
          )}
        </StyledLicenceWrapper>
      </Accordion>
      <Accordion
        title={t('licence.licencePrintoutSettings.label')}
        name="licencePrintoutSettings"
        key="licencePrintoutSettings"
        alwaysExpanded
        hideExpandIcon
        buttons={
          <Button
            label={
              licence.printoutSettings ? t('general.edit.button') : t('general.add.button')
            }
            id="licencePrintoutSettingsEdit"
            variant={EButtonVariants.outlined}
            onClick={() =>
              navigate(ERouteLinks.LicencePrintoutSettings.replace(':id', id || ''))
            }
            size="small"
            sx={{ minWidth: '94px' }}
          />
        }
      >
        <StyledLicenceWrapper size={{ xs: 12 }} container>
          {!licence.printoutSettings && (
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2">
                {t('licence.updatePrintoutSettings.empty.text')}
              </Typography>
            </Grid>
          )}
          {licence?.printoutSettings && (
            <Grid size={{ xs: 12 }} container spacing={2}>
              <Grid size={{ xs: 12 }} container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <FormLabel
                    name="generalLicenceInformation"
                    label={t('licence.generalLicenceInformation.label')}
                  />
                  {generalFields && generalFields?.length > 0
                    ? generalFields?.map((item: string) => (
                        <FormCheckbox
                          name={`printoutSettings.general.${item}`}
                          label={t(
                            ELicenceCardInformationFormLabels?.[
                              item as keyof typeof ELicenceCardInformationFormLabels
                            ],
                          )}
                          control={control}
                          errors={errors}
                          withValidation={false}
                          disabled
                          labelEnable
                          checked={!!filteredGeneralDataFields.includes(item)}
                        />
                      ))
                    : noOptionsSelectedText}
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <FormLabel
                    name="licenceConditions"
                    label={t('licence.licenceConditions.label')}
                  />
                  {conditionsFields && conditionsFields?.length > 0
                    ? conditionsFields?.map((item: string) => (
                        <FormCheckbox
                          name={`printoutSettings.conditions.${item}`}
                          label={t(
                            ELicenceCardInformationFormLabels?.[
                              item as keyof typeof ELicenceCardInformationFormLabels
                            ],
                          )}
                          control={control}
                          errors={errors}
                          withValidation={false}
                          disabled
                          labelEnable
                          checked={!!filteredConditionsDataFields.includes(item)}
                        />
                      ))
                    : noOptionsSelectedText}
                </Grid>
              </Grid>
              <Grid size={{ xs: 12 }} container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <FormLabel name="licenceOwner" label={t('licence.licenceOwner.label')} />
                  {ownerFields && ownerFields?.length > 0
                    ? ownerFields?.map((item: string) => (
                        <FormCheckbox
                          name={`printoutSettings.owner.${item}`}
                          label={t(
                            ELicenceCardInformationFormLabels?.[
                              item as keyof typeof ELicenceCardInformationFormLabels
                            ],
                          )}
                          control={control}
                          errors={errors}
                          withValidation={false}
                          disabled
                          labelEnable
                          checked={!!filteredOwnerDataFields.includes(item)}
                        />
                      ))
                    : noOptionsSelectedText}
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <FormLabel
                    name="licenceWeaponData"
                    label={t('licence.licenceWeaponData.label')}
                  />
                  {weaponDataFields && weaponDataFields?.length > 0
                    ? weaponDataFields?.map((item: string) => (
                        <FormCheckbox
                          name={`printoutSettings.weaponData.${item}`}
                          label={t(
                            ELicenceCardInformationFormLabels?.[
                              item as keyof typeof ELicenceCardInformationFormLabels
                            ],
                          )}
                          control={control}
                          errors={errors}
                          withValidation={false}
                          disabled
                          labelEnable
                          checked={!!filteredWeaponDataFields.includes(item)}
                        />
                      ))
                    : noOptionsSelectedText}
                </Grid>
              </Grid>
            </Grid>
          )}
        </StyledLicenceWrapper>
      </Accordion>
      {/* <Accordion
        title={t('licence.licenceCardInformation.label')}
        name="licenceCardInformation"
        key="licenceCardInformation"
        alwaysExpanded
        hideExpandIcon
      >
        <StyledLicenceWrapper container size={{ xs: 12 }}>
          <Grid size={{ xs: 12, lg: 4 }}>
            <FormLabel name="licenceData" label={t('licence.licenceData.label')} />
            {filteredLicenceDataFields.length > 0
              ? filteredLicenceDataFields.map((item: string) => (
                  <FormCheckbox
                    name={`documentInformation.${item}`}
                    label={t(
                      ELicenceCardInformationFormLabels?.[
                        item as keyof typeof ELicenceCardInformationFormLabels
                      ],
                    )}
                    control={control}
                    errors={errors}
                    withValidation={false}
                    disabled
                    labelEnable
                  />
                ))
              : noOptionsSelectedText}
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <FormLabel name="legalEntityData" label={t('licence.legalEntityData.label')} />
            {filteredLegalEntityDataFields.length > 0
              ? filteredLegalEntityDataFields.map((item: string) => (
                  <FormCheckbox
                    name={`documentInformation.${item}`}
                    label={t(
                      ELicenceCardInformationFormLabels?.[
                        item as keyof typeof ELicenceCardInformationFormLabels
                      ],
                    )}
                    control={control}
                    errors={errors}
                    withValidation={false}
                    disabled
                    labelEnable
                  />
                ))
              : noOptionsSelectedText}
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <FormLabel name="weaponData" label={t('licence.weaponData.label')} />
            {filteredWeaponDataFields.length > 0
              ? filteredWeaponDataFields.map((item: string) => (
                  <FormCheckbox
                    name={`documentInformation.${item}`}
                    label={t(
                      ELicenceCardInformationFormLabels?.[
                        item as keyof typeof ELicenceCardInformationFormLabels
                      ],
                    )}
                    control={control}
                    errors={errors}
                    withValidation={false}
                    disabled
                    labelEnable
                  />
                ))
              : noOptionsSelectedText}
          </Grid>
        </StyledLicenceWrapper>
      </Accordion> */}
      <Accordion
        title={t('licence.applicationProcess.label')}
        name="applicationProcess"
        key="applicationProcess"
        alwaysExpanded
        hideExpandIcon
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              id="licence_configuration_details-add-new-price"
              label={t('licence.addApplicationProcess.button')}
              permission={EPermission.MANAGE_WEAPON_LICENCE_CONFIGURATOR}
              variant={EButtonVariants.contained}
              onClick={() =>
                navigate(
                  ERouteLinks.AddLicenceTemplateApplicationProcessPage.replace(
                    ':id',
                    licence.uniqueId ?? '',
                  ),
                )
              }
              icon={EIconTypes.plus}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            />
          </Box>
          {applicationProcessAlert && (
            <Grid size={{ xs: 12 }}>
              <Alert
                text={applicationProcessAlert.text}
                variant={applicationProcessAlert.variant}
              />
            </Grid>
          )}

          <FormModal
            open={!!processIdDelete}
            onClickClose={() => setProcessIdDelete(null)}
            approveButtonLabel="general.yes.label"
            cancelButtonLabel="general.no.label"
            title={t('applicationProcess.remove.text')}
            approveButtonSubmit
            onSubmit={handleDeleteApplicationProcess}
          />

          {isProcessingPricingLoading ? (
            <Loader isLoading={true} />
          ) : (
            <Grid container>
              {isDesktop ? (
                <Table
                  columns={LicenceApplicationProcessTableColumns({
                    handleView: (processId: string) =>
                      navigate(
                        ERouteLinks.LicenceTemplateApplicationProcessPage.replace(
                          ':id',
                          licence.uniqueId ?? '',
                        ).replace(':processId', processId),
                      ),
                    handleDelete: (processId: string) => setProcessIdDelete(processId),
                    t,
                  })}
                  dataSource={mapLicenceApplicationProcessToDataSource(
                    licencePrices,
                    applicationProcessTypes,
                    applicationProcessStatus,
                    currency,
                  )}
                />
              ) : (
                <CardList
                  items={mapLicenceApplicationProcessToDataSource(
                    licencePrices,
                    applicationProcessTypes,
                    applicationProcessStatus,
                    currency,
                  )}
                  render={(data, index) => (
                    <LicenceApplicationProcessTableCard
                      data={data}
                      key={index}
                      handleDelete={(processId: string) => setProcessIdDelete(processId)}
                      handleView={(processId: string) =>
                        navigate(
                          ERouteLinks.LicenceTemplateApplicationProcessPage.replace(
                            ':id',
                            licence.uniqueId ?? '',
                          ).replace(':processId', processId),
                        )
                      }
                    />
                  )}
                />
              )}
            </Grid>
          )}
        </Box>
      </Accordion>
    </>
  );
};

export { DetailLicenceTemplatePage };
